import { useEffect, useRef, useState } from 'react'
import '../styles/transmission-room.css'

const MEDIA_ROOT = `${import.meta.env.BASE_URL}media/`
const FILM_SOURCE = `${MEDIA_ROOT}nova-film.mp4`
const FILM_POSTER = `${MEDIA_ROOT}nova-film-poster.webp`

type TransmissionRoomProps = {
  motionEnabled: boolean
}

function timecode(seconds: number) {
  const wholeSeconds = Math.max(0, Math.floor(seconds))
  return `${String(Math.floor(wholeSeconds / 60)).padStart(2, '0')}:${String(wholeSeconds % 60).padStart(2, '0')}`
}

export function TransmissionRoom({ motionEnabled }: TransmissionRoomProps) {
  const sectionRef = useRef<HTMLElement>(null)
  const screenRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const [hasStarted, setHasStarted] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [hasEnded, setHasEnded] = useState(false)
  const [duration, setDuration] = useState<number | null>(null)
  const [elapsed, setElapsed] = useState(0)
  const [playbackMessage, setPlaybackMessage] = useState('')

  useEffect(() => {
    const section = sectionRef.current
    const screen = screenRef.current
    const video = videoRef.current
    if (!section || !screen || !video) return

    // Ambient decoration and playback stop when there is no screen to watch.
    // Coming back never resumes sound without another deliberate play action.
    const observer = new IntersectionObserver(
      ([entry]) => {
        section.dataset.inView = String(entry.isIntersecting)
        if (!entry.isIntersecting) video.pause()
      },
      { threshold: 0 },
    )
    const handleVisibility = () => {
      section.dataset.pageVisible = String(!document.hidden)
      if (document.hidden) video.pause()
    }

    handleVisibility()
    observer.observe(screen)
    document.addEventListener('visibilitychange', handleVisibility)

    return () => {
      observer.disconnect()
      document.removeEventListener('visibilitychange', handleVisibility)
      video.pause()
    }
  }, [])

  const playFilm = async () => {
    const video = videoRef.current
    if (!video) return

    setHasStarted(true)
    setHasEnded(false)
    setPlaybackMessage('')
    if (video.ended) video.currentTime = 0

    try {
      await video.play()
      video.focus({ preventScroll: true })
    } catch (error) {
      // Leaving the section while play() resolves is a normal pause, not an error.
      if (error instanceof DOMException && error.name === 'AbortError') return
      setPlaybackMessage(
        'Playback did not start. Try the player controls or open the film directly.',
      )
      video.focus({ preventScroll: true })
    }
  }

  const showPlayPrompt = !hasStarted || hasEnded

  return (
    <section
      ref={sectionRef}
      className="transmission-room"
      id="film"
      aria-labelledby="film-title"
      data-motion={motionEnabled ? 'on' : 'off'}
      data-playing={isPlaying}
    >
      <div className="transmission-room__masthead">
        <p className="transmission-room__eyebrow">
          <span className="transmission-room__status-dot" aria-hidden="true" />
          Transmission recovered
        </p>
        <p className="transmission-room__archive">Nova archive / Original film</p>
      </div>

      <div className="transmission-room__heading">
        <h2 id="film-title">
          <span>The night</span>
          <span>it fell.</span>
        </h2>
        <div className="transmission-room__intro">
          <span className="transmission-room__coordinate" aria-hidden="true">
            <svg viewBox="0 0 40 40" focusable="false">
              <circle cx="20" cy="20" r="11" />
              <path d="M20 0v13m0 14v13M0 20h13m14 0h13" />
              <circle cx="20" cy="20" r="2" />
            </svg>
          </span>
          <p>
            Before the seal.
            <br />
            Before the three signals.
            <br />
            There was a light in the dark.
          </p>
        </div>
      </div>

      <figure className="transmission-room__reel">
        <div className="transmission-room__frame-meta">
          <span>Source / NVA–01</span>
          <span className="transmission-room__timecode" aria-hidden="true">
            {timecode(elapsed)} <span>/</span> {duration === null ? '––:––' : timecode(duration)}
          </span>
          <span className="transmission-room__playback-state">
            {isPlaying ? 'Playing' : hasEnded ? 'End of transmission' : hasStarted ? 'Paused' : 'Awaiting playback'}
          </span>
        </div>

        <div ref={screenRef} className="transmission-room__screen">
          <video
            ref={videoRef}
            className="transmission-room__video"
            controls={hasStarted}
            tabIndex={hasStarted ? 0 : -1}
            playsInline
            preload="metadata"
            poster={FILM_POSTER}
            aria-label="Nova Energy campaign film: The night it fell"
            aria-describedby="film-description"
            onLoadedMetadata={(event) => {
              const length = event.currentTarget.duration
              setDuration(Number.isFinite(length) && length > 0 ? length : null)
            }}
            onTimeUpdate={(event) => setElapsed(Math.floor(event.currentTarget.currentTime))}
            onPlay={() => {
              setHasStarted(true)
              setHasEnded(false)
              setIsPlaying(true)
              setPlaybackMessage('')
            }}
            onPause={() => setIsPlaying(false)}
            onEnded={() => {
              setIsPlaying(false)
              setHasEnded(true)
            }}
            onError={() => {
              setIsPlaying(false)
              setPlaybackMessage('The film could not load. Open the original file to try again.')
            }}
          >
            <source src={FILM_SOURCE} type="video/mp4" />
            Your browser cannot play this video. <a href={FILM_SOURCE}>Open the Nova film</a>.
          </video>

          {showPlayPrompt ? (
            <div className="transmission-room__standby">
              <div className="transmission-room__scanlines" aria-hidden="true" />
              <div className="transmission-room__scan" aria-hidden="true" />
              <span className="transmission-room__corner transmission-room__corner--tl" aria-hidden="true" />
              <span className="transmission-room__corner transmission-room__corner--br" aria-hidden="true" />
              <span className="transmission-room__frame-label" aria-hidden="true">
                Recovered footage
              </span>
              <button
                className="transmission-room__play"
                type="button"
                onClick={() => { void playFilm() }}
                aria-label={hasEnded ? 'Replay the Nova Energy campaign film' : 'Play the Nova Energy campaign film'}
              >
                <span className="transmission-room__play-icon" aria-hidden="true">
                  <svg viewBox="0 0 28 28" focusable="false">
                    <path d="m10 6 12 8-12 8V6Z" />
                  </svg>
                </span>
                <span className="transmission-room__play-copy">
                  <strong>{hasEnded ? 'Replay transmission' : 'Watch transmission'}</strong>
                  <span>{duration === null ? 'Nova Energy / Campaign film' : `${timecode(duration)} / Campaign film`}</span>
                </span>
              </button>
              <span className="transmission-room__standby-note" aria-hidden="true">
                Stolen from the skies.
              </span>
            </div>
          ) : null}
        </div>

        {playbackMessage ? (
          <p className="transmission-room__error" role="status">
            {playbackMessage}{' '}
            <a href={FILM_SOURCE}>Open film <span aria-hidden="true">↗</span></a>
          </p>
        ) : null}

        <figcaption className="transmission-room__caption" id="film-description">
          <p className="transmission-room__caption-label">The first trace</p>
          <p>
            An impossible object. An ultraviolet descent. The original Nova film
            follows the signal before it was captured, recreated and sealed
            under pressure.
          </p>
          <span className="transmission-room__endmark" aria-hidden="true">✦</span>
        </figcaption>
      </figure>
    </section>
  )
}
